import { useState, useEffect, useCallback } from "react";

interface NotificationHistoryPanelProps {
  fetchNotificationHistory: (params?: Record<string, unknown>) => Promise<any>;
  selectedClients: number[];
  isGlobalView: () => boolean;
}

export default function NotificationHistoryPanel({
  fetchNotificationHistory,
  selectedClients,
  isGlobalView,
}: NotificationHistoryPanelProps) {
  const [notificationHistory, setNotificationHistory] = useState<any[]>([]);
  const [notifHistoryLoading, setNotifHistoryLoading] = useState(false);
  const [notifHistoryCount, setNotifHistoryCount] = useState(0);
  const [notifHistoryPage, setNotifHistoryPage] = useState(0);
  const notifHistoryPageSize = 10;
  const [historySenderFilter, setHistorySenderFilter] = useState("all");
  const [historyClientPhoneFilter, setHistoryClientPhoneFilter] = useState("");
  const [historySenderOptions, setHistorySenderOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [historyTypeFilter, setHistoryTypeFilter] = useState("all");

  const historyLinkedClientIds = selectedClients.length > 0 ? selectedClients : undefined;
  const isHistoryLinked = selectedClients.length > 0;

  const loadNotificationHistory = useCallback(async () => {
    setNotifHistoryLoading(true);
    const params: any = {
      limit: notifHistoryPageSize,
      offset: notifHistoryPage * notifHistoryPageSize,
      type: historyTypeFilter !== "all" ? historyTypeFilter : undefined,
    };
    if (historyLinkedClientIds) {
      params.client_ids = historyLinkedClientIds;
    } else {
      if (historySenderFilter !== "all") params.sender = historySenderFilter;
      if (historyClientPhoneFilter.trim()) params.client_phone = historyClientPhoneFilter.trim();
    }
    const res = await fetchNotificationHistory(params);
    setNotifHistoryLoading(false);
    if (res?.success) {
      setNotificationHistory(res.notifications || []);
      setNotifHistoryCount(res.count || 0);
      const senders: Array<{ value: string; label: string }> = (res.notifications || [])
        .map((n: any) => n.sender_name)
        .filter(Boolean)
        .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)
        .map((name: string) => ({ value: name, label: name }));
      if (senders.length > 0) {
        setHistorySenderOptions((prev) => {
          const existing = new Set(prev.map((p) => p.value));
          const newOpts = senders.filter((s) => !existing.has(s.value));
          return newOpts.length > 0 ? [...prev, ...newOpts] : prev;
        });
      }
    }
  }, [fetchNotificationHistory, notifHistoryPage, historySenderFilter, historyClientPhoneFilter, historyTypeFilter, historyLinkedClientIds]);

  useEffect(() => { loadNotificationHistory(); }, [loadNotificationHistory]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#1a3a5c]">通知历史</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">查看已推送的通知记录</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isGlobalView() && !isHistoryLinked && (
            <select value={historySenderFilter} onChange={(e) => { setHistorySenderFilter(e.target.value); setNotifHistoryPage(0); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">全部发送人</option>
              {historySenderOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          )}
          {!isHistoryLinked && (
            <div className="relative">
              <i className="ri-phone-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
              <input type="text" value={historyClientPhoneFilter}
                onChange={(e) => { setHistoryClientPhoneFilter(e.target.value); setNotifHistoryPage(0); }}
                placeholder="客户编码筛选"
                className="pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] bg-white w-32" />
            </div>
          )}
          <button onClick={loadNotificationHistory}
            className="px-3 py-2 bg-gray-50 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
            <i className="ri-refresh-line"></i>刷新
          </button>
        </div>
      </div>
      {isHistoryLinked && (
        <p className="text-[11px] text-[#c9a96e] flex items-center gap-1">
          <i className="ri-link-m"></i>已关联通知推送选中客户（{selectedClients.length} 位），显示相关通知历史
        </p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { value: "all", label: "全部" },
          { value: "info", label: "系统" },
          { value: "success", label: "好消息" },
          { value: "warning", label: "提醒" },
          { value: "error", label: "紧急" },
        ].map((t) => (
          <button key={t.value} onClick={() => { setHistoryTypeFilter(t.value); setNotifHistoryPage(0); }}
            className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
              historyTypeFilter === t.value
                ? t.value === "success" ? "bg-emerald-500 text-white"
                : t.value === "warning" ? "bg-amber-500 text-white"
                : t.value === "error" ? "bg-red-500 text-white"
                : "bg-[#1a3a5c] text-white"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {notifHistoryLoading ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
        ) : notificationHistory.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            <i className="ri-time-line text-2xl mb-2 block text-gray-300"></i>
            暂无发送记录
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notificationHistory.map((n) => (
              <div key={n.id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-medium text-[#1a3a5c] truncate">{n.title}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
                        n.type === "success" ? "bg-emerald-50 text-emerald-600" :
                        n.type === "warning" ? "bg-amber-50 text-amber-600" :
                        n.type === "error" ? "bg-red-50 text-red-600" :
                        "bg-[#1a3a5c]/5 text-[#1a3a5c]"}`}>
                        {n.type === "success" ? "好消息" : n.type === "warning" ? "提醒" : n.type === "error" ? "紧急" : "系统"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-1 mb-1.5">{n.content}</p>
                    <div className="flex items-center gap-2 text-[11px] text-gray-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line text-[10px]"></i>
                        {n.client_name || n.client?.name || "未命名"} · {n.client_phone || n.client?.phone || "-"}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-send-plane-line text-[10px]"></i>
                        {n.sender_name || "系统"}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-[10px]"></i>
                        {new Date(n.scheduled_at || n.created_at).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {notifHistoryCount > notifHistoryPageSize && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">共 {notifHistoryCount} 条 · 第 {notifHistoryPage + 1} / {Math.ceil(notifHistoryCount / notifHistoryPageSize)} 页</p>
            <div className="flex gap-2">
              <button onClick={() => setNotifHistoryPage((p) => Math.max(0, p - 1))} disabled={notifHistoryPage === 0} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"><i className="ri-arrow-left-s-line"></i></button>
              <button onClick={() => setNotifHistoryPage((p) => p + 1)} disabled={(notifHistoryPage + 1) * notifHistoryPageSize >= notifHistoryCount} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"><i className="ri-arrow-right-s-line"></i></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}